// Offline Configuration Manager
// Controls all offline features based on environment variables

export interface OfflineConfig {
  // Main offline mode toggle
  enableOfflineMode: boolean;

  // Service Worker configuration
  enableServiceWorker: boolean;
  serviceWorkerScope: string;
  serviceWorkerUpdateViaCache: 'imports' | 'all' | 'none';

  // IndexedDB configuration
  enableIndexedDB: boolean;
  indexedDBName: string;
  indexedDBVersion: number;

  // Background sync configuration
  enableBackgroundSync: boolean;
  syncInterval: number; // in milliseconds
  maxRetryAttempts: number;

  // Cache strategy
  cacheStrategy: 'cache-first' | 'network-first' | 'cache-only' | 'network-only';

  // Development settings
  isDevelopment: boolean;
  isProduction: boolean;
  enableDebugLogs: boolean;

  // Feature flags
  enableOfflineAuth: boolean;
  enableOfflineData: boolean;
  enableOfflineAssets: boolean;
}

class OfflineConfigManager {
  private static instance: OfflineConfigManager;
  private config: OfflineConfig;

  private constructor() {
    this.config = this.loadConfig();
    this.validateConfig();
    this.logConfig();
  }

  static getInstance(): OfflineConfigManager {
    if (!OfflineConfigManager.instance) {
      OfflineConfigManager.instance = new OfflineConfigManager();
    }
    return OfflineConfigManager.instance;
  }

  private loadConfig(): OfflineConfig {
    // Helper function to parse boolean environment variables
    const parseBoolean = (value: string | undefined, defaultValue: boolean = false): boolean => {
      if (!value) return defaultValue;
      return value.toLowerCase() === 'true' || value === '1';
    };

    // Helper function to parse number environment variables
    const parseNumber = (value: string | undefined, defaultValue: number): number => {
      if (!value) return defaultValue;
      const parsed = parseInt(value, 10);
      return isNaN(parsed) ? defaultValue : parsed;
    };

    const isDev = import.meta.env.DEV;
    const isProd = import.meta.env.PROD;

    return {
      // Main offline mode toggle - controlled by environment variable
      enableOfflineMode: parseBoolean(import.meta.env.VITE_ENABLE_OFFLINE_MODE, false),

      // Service Worker configuration
      enableServiceWorker: parseBoolean(import.meta.env.VITE_ENABLE_SERVICE_WORKER, true),
      serviceWorkerScope: import.meta.env.VITE_SW_SCOPE || '/',
      serviceWorkerUpdateViaCache: (import.meta.env.VITE_SW_UPDATE_VIA_CACHE as any) || 'none',

      // IndexedDB configuration
      enableIndexedDB: parseBoolean(import.meta.env.VITE_ENABLE_INDEXEDDB, true),
      indexedDBName: import.meta.env.VITE_INDEXEDDB_NAME || 'altzero_offline_db',
      indexedDBVersion: parseNumber(import.meta.env.VITE_INDEXEDDB_VERSION, 1),

      // Background sync configuration
      enableBackgroundSync: parseBoolean(import.meta.env.VITE_ENABLE_BACKGROUND_SYNC, true),
      syncInterval: parseNumber(import.meta.env.VITE_SYNC_INTERVAL, 30000), // 30 seconds
      maxRetryAttempts: parseNumber(import.meta.env.VITE_MAX_RETRY_ATTEMPTS, 3),

      // Cache strategy
      cacheStrategy: (import.meta.env.VITE_OFFLINE_CACHE_STRATEGY as any) || 'cache-first',

      // Development settings
      isDevelopment: isDev,
      isProduction: isProd,
      enableDebugLogs: parseBoolean(import.meta.env.VITE_ENABLE_DEBUG_LOGS, false),

      // Feature flags
      enableOfflineAuth: parseBoolean(import.meta.env.VITE_ENABLE_OFFLINE_AUTH, true),
      enableOfflineData: parseBoolean(import.meta.env.VITE_ENABLE_OFFLINE_DATA, true),
      enableOfflineAssets: parseBoolean(import.meta.env.VITE_ENABLE_OFFLINE_ASSETS, true),
    };
  }

  private validateConfig(): void {
    const { config } = this;

    // Validate cache strategy
    const validCacheStrategies = ['cache-first', 'network-first', 'cache-only', 'network-only'];
    if (!validCacheStrategies.includes(config.cacheStrategy)) {
      console.warn(`[OfflineConfig] Invalid cache strategy: ${config.cacheStrategy}. Using 'cache-first'.`);
      config.cacheStrategy = 'cache-first';
    }

    // Validate service worker update via cache
    const validUpdateViaCache = ['imports', 'all', 'none'];
    if (!validUpdateViaCache.includes(config.serviceWorkerUpdateViaCache)) {
      console.warn(`[OfflineConfig] Invalid updateViaCache: ${config.serviceWorkerUpdateViaCache}. Using 'none'.`);
      config.serviceWorkerUpdateViaCache = 'none';
    }

    // Validate sync interval (minimum 5 seconds)
    if (config.syncInterval < 5000) {
      console.warn(`[OfflineConfig] Sync interval too low: ${config.syncInterval}ms. Using 5000ms.`);
      config.syncInterval = 5000;
    }

    // Validate max retry attempts
    if (config.maxRetryAttempts < 1) {
      console.warn(`[OfflineConfig] Max retry attempts too low: ${config.maxRetryAttempts}. Using 1.`);
      config.maxRetryAttempts = 1;
    }
  }

  private logConfig(): void {
    if (!this.config.enableDebugLogs) return;

    console.group('🔧 [OfflineConfig] Configuration Loaded');
    console.log('📱 Offline Mode:', this.config.enableOfflineMode ? '✅ Enabled' : '❌ Disabled');
    console.log('⚙️ Service Worker:', this.config.enableServiceWorker ? '✅ Enabled' : '❌ Disabled');
    console.log('💾 IndexedDB:', this.config.enableIndexedDB ? '✅ Enabled' : '❌ Disabled');
    console.log('🔄 Background Sync:', this.config.enableBackgroundSync ? '✅ Enabled' : '❌ Disabled');
    console.log('📦 Cache Strategy:', this.config.cacheStrategy);
    console.log('🔧 Environment:', this.config.isDevelopment ? 'Development' : 'Production');
    console.groupEnd();
  }

  // Public getters
  get offlineMode(): boolean {
    return this.config.enableOfflineMode;
  }

  get serviceWorker(): boolean {
    return this.config.enableOfflineMode && this.config.enableServiceWorker;
  }

  get indexedDB(): boolean {
    return this.config.enableOfflineMode && this.config.enableIndexedDB;
  }

  get backgroundSync(): boolean {
    return this.config.enableOfflineMode && this.config.enableBackgroundSync;
  }

  get cacheStrategy(): string {
    return this.config.cacheStrategy;
  }

  get syncInterval(): number {
    return this.config.syncInterval;
  }

  get maxRetryAttempts(): number {
    return this.config.maxRetryAttempts;
  }

  get debugLogs(): boolean {
    return this.config.enableDebugLogs;
  }

  get isDev(): boolean {
    return this.config.isDevelopment;
  }

  get isProd(): boolean {
    return this.config.isProduction;
  }

  // Get full config
  getConfig(): OfflineConfig {
    return { ...this.config };
  }

  // Feature-specific getters
  get offlineAuth(): boolean {
    return this.config.enableOfflineMode && this.config.enableOfflineAuth;
  }

  get offlineData(): boolean {
    return this.config.enableOfflineMode && this.config.enableOfflineData;
  }

  get offlineAssets(): boolean {
    return this.config.enableOfflineMode && this.config.enableOfflineAssets;
  }

  // Service Worker specific config
  getServiceWorkerConfig() {
    return {
      scope: this.config.serviceWorkerScope,
      updateViaCache: this.config.serviceWorkerUpdateViaCache,
      enabled: this.serviceWorker
    };
  }

  // IndexedDB specific config
  getIndexedDBConfig() {
    return {
      name: this.config.indexedDBName,
      version: this.config.indexedDBVersion,
      enabled: this.indexedDB
    };
  }

  // Check if offline features should be initialized
  shouldInitializeOfflineFeatures(): boolean {
    return this.config.enableOfflineMode;
  }

  // Check if service worker should be registered
  shouldRegisterServiceWorker(): boolean {
    return this.serviceWorker && 'serviceWorker' in navigator;
  }

  // Check if IndexedDB should be initialized
  shouldInitializeIndexedDB(): boolean {
    return this.indexedDB && 'indexedDB' in window;
  }
}

// Export singleton instance
export const offlineConfig = OfflineConfigManager.getInstance();



